import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError, assetUrl } from "@/lib/api";
import { StoreHeader, StoreFooter, fontStack, textOn, ensureFontsLoaded } from "@/lib/storeBlocks";
import { Diamond, CircleNotch, ShoppingCart, Sparkle, ArrowLeft, CaretLeft, CaretRight, Cube } from "@phosphor-icons/react";
import { toast } from "sonner";

const DEF = {
  image_position: "left", image_width: 50, gallery_style: "thumbs", rounded: true,
  title_size: 34, show_description: true, description_size: 15, show_price: true,
  show_offer_badge: true, button_style: "pill", show_request: true,
  request_label: "¿Alguna petición especial? (opcional)",
};

export default function ProductPage() {
  const { slug, pid } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [err, setErr] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [show3d, setShow3d] = useState(true);
  const [variant, setVariant] = useState("");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/public/products/${pid}`, { withCredentials: false }),
      api.get(`/public/store/${slug}`, { withCredentials: false }).catch(() => null),
    ]).then(([pr, st]) => {
      if (!alive) return;
      setProduct(pr.data);
      setStore(st?.data?.store || pr.data.store || null);
      setVariant((pr.data.variants || [])[0]?.label || "");
    }).catch((e) => alive && setErr(apiError(e)));
    return () => { alive = false; };
  }, [pid, slug]);

  const site = useMemo(() => (store?.site) || {}, [store]);
  const cfg = useMemo(() => ({ ...DEF, ...((site.product_page) || (product?.store?.product_page) || {}) }), [site, product]);
  const accent = store?.accent || product?.store?.accent || "#C6A15B";
  const bg = store?.theme_color || product?.store?.theme_color || "#0B0B0F";
  const headingFont = fontStack(site.font || "serif");
  const bodyFont = site.body_font ? fontStack(site.body_font) : undefined;
  const palette = site.palette || {};

  useEffect(() => { ensureFontsLoaded([site.font || "serif", site.body_font].filter(Boolean)); }, [site]);

  const ctx = useMemo(() => ({
    store: store || {}, slug, accent, bg, headingFont, bodyFont,
    titleColor: palette.text || undefined, btnBg: palette.btnBg || undefined, btnText: palette.btnText || undefined,
    linkColor: palette.link || undefined, interactive: true, t: (k, d) => d || k, products: [],
  }), [store, slug, accent, bg, headingFont, bodyFont, palette]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-neutral-400" style={{ background: bg }}>{err}</div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center" style={{ background: bg }}><CircleNotch size={30} className="animate-spin" style={{ color: accent }} /></div>;

  const isSimple = product.catalog_type && product.catalog_type !== "custom3d";
  const canCustomize = product.catalog_type === "simple" && product.simple_custom;
  const hasPersonalization = !isSimple;
  const variants = product.variants || [];
  const images = (product.gallery_urls && product.gallery_urls.length ? product.gallery_urls : [product.base_url]).filter(Boolean);
  const chosenVariant = variants.find((v) => String(v.label) === String(variant));
  const basePrice = Number((chosenVariant && chosenVariant.price) || product.price || 0);
  const pct = Number(product.sale_percent || 0);
  const onSale = pct > 0 && basePrice > 0;
  const salePrice = onSale ? Math.round(basePrice * (1 - pct / 100) * 100) / 100 : basePrice;

  const rad = cfg.rounded ? "1rem" : "0";
  const btnRad = cfg.button_style === "square" ? "0.5rem" : "9999px";
  const textCol = palette.text || textOn(bg);

  const addToCart = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem(`hephy_cart_${slug}`) || undefined;
      const { data } = await api.post("/public/cart/add", {
        token, slug, product_id: product.id, metal_id: "", variant_label: variant,
        quantity: qty, request_note: note,
      }, { withCredentials: false });
      localStorage.setItem(`hephy_cart_${slug}`, data.token);
      toast.success("Añadido al carrito");
      navigate(`/tienda/${slug}/carrito`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const personalize = () => navigate(`/embed/${product.id}?store=${slug}`);

  const imgCol = (
    <div className="w-full" data-testid="product-media">
      <div className="w-full overflow-hidden bg-white/5 border border-white/10 relative" style={{ borderRadius: rad, aspectRatio: "1 / 1" }}>
        {hasPersonalization && show3d ? (
          <>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none" style={{ color: accent }}>
              <Cube size={46} weight="duotone" style={{ opacity: 0.5 }} />
              <span className="text-xs opacity-60">Vista 3D</span>
            </div>
            <iframe title={`3d-${product.id}`} src={`/embed/${product.id}?store=${slug}&showcase=1`} className="absolute inset-0 w-full h-full border-0" scrolling="no" data-testid="product-3d-viewer" />
          </>
        ) : images[imgIdx]
          ? <img src={assetUrl(images[imgIdx])} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          : <div className="w-full h-full flex items-center justify-center"><Diamond size={48} style={{ color: accent, opacity: 0.4 }} /></div>}
      </div>
      {(cfg.gallery_style === "thumbs" && (images.length > 1 || (hasPersonalization && images.length >= 1))) && (
        <div className="flex gap-2 mt-3 flex-wrap" data-testid="product-thumbs">
          {hasPersonalization && (
            <button onClick={() => setShow3d(true)} data-testid="product-thumb-3d"
              className="w-16 h-16 flex items-center justify-center overflow-hidden border transition-all" style={{ borderRadius: cfg.rounded ? "0.6rem" : 0, borderColor: show3d ? accent : "rgba(255,255,255,.15)" }}>
              <Cube size={22} style={{ color: accent }} weight="duotone" />
            </button>
          )}
          {images.map((im, i) => (
            <button key={i} onClick={() => { setShow3d(false); setImgIdx(i); }} data-testid={`product-thumb-${i}`}
              className="w-16 h-16 overflow-hidden border transition-all" style={{ borderRadius: cfg.rounded ? "0.6rem" : 0, borderColor: (!show3d && i === imgIdx) ? accent : "rgba(255,255,255,.15)" }}>
              <img src={assetUrl(im)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const infoCol = (
    <div className="w-full" style={{ color: textCol }} data-testid="product-info">
      {onSale && cfg.show_offer_badge && (
        <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full mb-3" style={{ background: accent, color: textOn(accent) }} data-testid="product-offer-badge">
          <Sparkle size={13} weight="fill" /> ¡Oferta −{pct}%!
        </span>
      )}
      <h1 className="font-bold leading-tight" style={{ fontSize: cfg.title_size, fontFamily: headingFont }} data-testid="product-title">{product.name}</h1>

      {cfg.show_price && basePrice > 0 && (
        <div className="flex items-end gap-3 mt-3" data-testid="product-price">
          <span className="font-bold" style={{ fontSize: Math.round(cfg.title_size * 0.75), color: onSale ? accent : textCol }}>${salePrice.toLocaleString()}</span>
          {onSale && <span className="text-lg line-through opacity-50" data-testid="product-compare-price">${basePrice.toLocaleString()}</span>}
          {onSale && <span className="text-xs font-semibold text-emerald-400 mb-1.5">Ahorras ${(basePrice - salePrice).toLocaleString()}</span>}
        </div>
      )}
      {cfg.show_price && basePrice <= 0 && <div className="mt-3 text-lg font-semibold" style={{ color: accent }}>Precio a personalizar</div>}

      {cfg.show_description && product.description && (
        <p className="mt-4 whitespace-pre-line opacity-85 leading-relaxed" style={{ fontSize: cfg.description_size, fontFamily: bodyFont }} data-testid="product-description">{product.description}</p>
      )}

      {isSimple && !canCustomize && variants.length > 0 && (
        <div className="mt-5">
          <label className="text-xs opacity-70 block mb-1.5">{product.variant_name || "Opción"}</label>
          <select value={variant} onChange={(e) => setVariant(e.target.value)} data-testid="product-variant-select"
            className="w-full max-w-xs h-11 px-3 text-sm bg-white/5 border border-white/20 outline-none" style={{ borderRadius: cfg.rounded ? "0.6rem" : 0, color: textCol }}>
            {variants.map((v) => <option key={v.label} value={v.label} className="bg-[#1a1a1a] text-white">{v.label}{v.price ? ` — $${Number(v.price).toLocaleString()}` : ""}</option>)}
          </select>
        </div>
      )}

      {isSimple && !canCustomize && (
        <div className="mt-5 flex items-center gap-3">
          <label className="text-xs opacity-70">Cantidad</label>
          <div className="flex items-center border border-white/20 overflow-hidden" style={{ borderRadius: cfg.rounded ? "9999px" : 0 }}>
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 hover:bg-white/10" data-testid="product-qty-minus">−</button>
            <span className="w-10 text-center text-sm" data-testid="product-qty">{qty}</span>
            <button onClick={() => setQty((q) => Math.min(20, q + 1))} className="w-9 h-9 hover:bg-white/10" data-testid="product-qty-plus">+</button>
          </div>
        </div>
      )}

      {cfg.show_request && !canCustomize && (
        <div className="mt-5">
          <label className="text-xs opacity-70 block mb-1.5" data-testid="product-request-label">{cfg.request_label}</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} data-testid="product-request-input"
            placeholder="Ej: Grabar la fecha 10/06, envolver para regalo…"
            className="w-full px-3 py-2 text-sm bg-white/5 border border-white/20 outline-none focus:border-white/40" style={{ borderRadius: cfg.rounded ? "0.6rem" : 0, color: textCol }} />
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        {hasPersonalization ? (
          <button onClick={personalize} data-testid="product-personalize-btn"
            className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold transition-transform hover:-translate-y-0.5"
            style={{ borderRadius: btnRad, background: accent, color: textOn(accent) }}>
            <Sparkle size={17} weight="fill" /> Personalizar
          </button>
        ) : canCustomize ? (
          <button onClick={personalize} data-testid="product-personalize-btn"
            className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold transition-transform hover:-translate-y-0.5"
            style={{ borderRadius: btnRad, background: accent, color: textOn(accent) }}>
            <Sparkle size={17} weight="fill" /> Personalizar
          </button>
        ) : (
          <button onClick={addToCart} disabled={busy} data-testid="product-add-cart-btn"
            className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            style={{ borderRadius: btnRad, background: palette.btnBg || accent, color: palette.btnText || textOn(accent) }}>
            <ShoppingCart size={17} weight="fill" /> {busy ? "…" : "Agregar al carrito"}
          </button>
        )}
        <Link to={`/tienda/${slug}`} data-testid="product-back-link"
          className="inline-flex items-center gap-1.5 px-6 py-3.5 text-sm font-semibold border transition-colors hover:bg-white/5"
          style={{ borderRadius: btnRad, borderColor: "rgba(255,255,255,.2)", color: textCol }}>
          <ArrowLeft size={15} /> Seguir viendo
        </Link>
      </div>
      {product.catalog_type === "digital" && <p className="mt-4 text-xs opacity-60">Producto digital. Recibirás el acceso por correo tras la compra.</p>}
    </div>
  );

  const left = cfg.image_position === "right" ? infoCol : imgCol;
  const right = cfg.image_position === "right" ? imgCol : infoCol;
  const imgW = Math.max(30, Math.min(70, cfg.image_width || 50));
  const gridCols = cfg.image_position === "right" ? `${100 - imgW}fr ${imgW}fr` : `${imgW}fr ${100 - imgW}fr`;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: bg, color: textCol, fontFamily: headingFont }} data-testid="product-page">
      {site.header && site.header.enabled !== false && <StoreHeader header={site.header} ctx={ctx} />}
      <main className="flex-1 w-full max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="hidden md:grid gap-10 items-start" style={{ gridTemplateColumns: gridCols }}>
          {left}{right}
        </div>
        <div className="md:hidden space-y-6">{imgCol}{infoCol}</div>
      </main>
      {site.footer && site.footer.enabled !== false && <StoreFooter footer={site.footer} ctx={ctx} />}
    </div>
  );
}


